import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { listChantiers, deleteChantier, updateChantier } from "../services/api";
import ChantierCard from "./ChantierCard";
import Popup from "./Popup";
import { getChantierId } from '../utils/getId';

const ChantierList = () => {
  const [chantiers, setChantiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [deletePopup, setDeletePopup] = useState({
    isOpen: false,
    chantier: null,
  });
  const [search, setSearch] = useState("");
  const [searchNumAttachement, setSearchNumAttachement] = useState(""); // NEW
  const [searchAdresseExecution, setSearchAdresseExecution] = useState(""); // NEW
  const [searchNumeroCommande, setSearchNumeroCommande] = useState(""); // NEW: numero de commande
  const [filter, setFilter] = useState("actifs");
  const [dateStart, setDateStart] = useState(""); // Start date filter
  const [dateEnd, setDateEnd] = useState(""); // End date filter
  const [invoicePopup, setInvoicePopup] = useState({
    isOpen: false,
    chantier: null,
  });
  const [numBonFactureInput, setNumBonFactureInput] = useState(""); // new
  const [savingNumBonFacture, setSavingNumBonFacture] = useState(false); // new
  const [numBonFactureError, setNumBonFactureError] = useState(""); // new
  const [selectedChantier, setSelectedChantier] = useState(null);
  // Add state for popup stats
  const [popupCharges, setPopupCharges] = useState([]);
  const [popupLoading, setPopupLoading] = useState(false);
  const itemsPerPage = 9;

  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

  // Debug log to check environment variables
  console.log("Environment API URL:", process.env.REACT_APP_API_URL);
  console.log("API URL being used:", API_URL);

  const normalizeChantiers = useCallback((list) => {
    return Array.isArray(list)
      ? list.map((c) => ({ ...c, id: getChantierId(c) ?? c.id ?? null }))
      : [];
  }, []);

  // Ensure fetchChantiers depends on API_URL as well
  const fetchChantiers = useCallback(async () => {
    try {
      setLoading(true);
      const data = await listChantiers();
      setChantiers(normalizeChantiers(data));
    } catch (error) {
      console.error("Error fetching chantiers:", error);
    } finally {
      setLoading(false);
    }
  }, [normalizeChantiers]);

  useEffect(() => {
    fetchChantiers();
  }, [fetchChantiers]);

  // NEW: Fallback hydrate for "Plombier" if backend didn't enrich it
  useEffect(() => {
    const toLower = (s) => (s || "").toString().trim().toLowerCase();

    const findPlumberName = (charges) => {
      if (!Array.isArray(charges)) return null;
      for (const ch of charges) {
        if (!Array.isArray(ch?.personnel)) continue;
        if (toLower(ch?.type) !== 'charges de personnel') continue;
        for (const p of ch.personnel) {
          const pName = toLower(p?.prestationType || p?.prestation?.name || p?.prestation_name || p?.type);
          if (pName === 'plombier') {
            return p?.nom || p?.name || null;
          }
        }
      }
      return null;
    };

    const hydrateMissingPlombiers = async () => {
      const items = chantiers || [];
      // Only process items without a plumber yet
      const toProcess = items
        .map(c => ({ c, cid: c._id || c.id || getChantierId(c) }))
        .filter(({ c, cid }) => cid && !c.plombier);

      if (toProcess.length === 0) return;

      const updates = {};
      for (const { cid } of toProcess) {
        try {
          const res = await fetch(`${API_URL}/api/charges/chantier/${cid}`);
          if (!res.ok) continue;
          const data = await res.json();
          const plumberName = findPlumberName(data);
          if (plumberName) updates[cid] = plumberName;
        } catch {
          /* ignore network errors for fallback */
        }
      }

      if (Object.keys(updates).length > 0) {
        setChantiers(prev =>
          prev.map(c => {
            const cid = c._id || c.id || getChantierId(c);
            return updates[cid] ? { ...c, plombier: updates[cid] } : c;
          })
        );
      }
    };

    hydrateMissingPlombiers();
  }, [chantiers, API_URL]);

  const handleDelete = async (id) => {
    const chantier = chantiers.find((c) => c._id === id || c.id === id);
    setDeletePopup({
      isOpen: true,
      chantier: chantier,
    });
  };

  const confirmDelete = async () => {
    try {
      await deleteChantier(deletePopup.chantier._id || deletePopup.chantier.id);
      setChantiers(
        chantiers.filter(
          (chantier) => (chantier._id || chantier.id) !== (deletePopup.chantier._id || deletePopup.chantier.id)
        )
      );
      setDeletePopup({ isOpen: false, chantier: null });
    } catch (error) {
      console.error("Error deleting chantier:", error);
    }
  };

  const cancelDelete = () => {
    setDeletePopup({ isOpen: false, chantier: null });
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setCurrentPage(1);
  };

  const handleSearchNumAttachementChange = (e) => {
    setSearchNumAttachement(e.target.value);
    setCurrentPage(1);
  };
  const handleSearchAdresseExecutionChange = (e) => {
    setSearchAdresseExecution(e.target.value);
    setCurrentPage(1);
  };
  const handleSearchNumeroCommandeChange = (e) => {
    setSearchNumeroCommande(e.target.value);
    setCurrentPage(1);
  };

  const handleFilterChange = (e) => {
    setFilter(e.target.value);
    setCurrentPage(1);
  };

  const handleCloseChantier = async (id) => {
    try {
      await updateChantier(id, { etat: "fermé" });
      setChantiers((prev) =>
        prev.map((c) =>
          (c._id === id || c.id === id) ? { ...c, etat: "fermé" } : c
        )
      );
    } catch (error) {
      const msg =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Erreur lors de la fermeture du chantier.";
      console.log(msg);
    }
  };

  // NEW: set chantier to SAV from fermé
  const handleSetSav = async (id) => {
    try {
      await updateChantier(id, { etat: "sav" });
      setChantiers((prev) =>
        prev.map((c) =>
          (c._id === id || c.id === id) ? { ...c, etat: "sav" } : c
        )
      );
    } catch (error) {
      console.error("Erreur lors du passage en SAV:", error);
    }
  };

  // NEW: set chantier to ANNULÉ from en cours (force budget to 0)
  const handleSetAnnule = async (id) => {
    try {
      await updateChantier(id, { etat: "annulé", prixPrestation: 0 });
      setChantiers((prev) =>
        prev.map((c) =>
          (c._id === id || c.id === id)
            ? { ...c, etat: "annulé", prixPrestation: 0 }
            : c
        )
      );
    } catch (error) {
      console.error("Erreur lors de l'annulation du chantier:", error);
    }
  };

  // Add handler for invoice button
  const handleInvoiceClick = (chantier) => {
    setInvoicePopup({
      isOpen: true,
      chantier,
    });
    setNumBonFactureInput(chantier.numBonFacture || "");
    setNumBonFactureError("");
  };

  const closeInvoicePopup = () => {
    setInvoicePopup({ isOpen: false, chantier: null });
    setNumBonFactureInput("");
    setNumBonFactureError("");
  };

  // Delete numBonFacture (remove invoice & facturé substatus)
  const handleDeleteNumBonFacture = async () => {
    if (!invoicePopup.chantier) return;
    const chantierId = invoicePopup.chantier._id || invoicePopup.chantier.id;
    setSavingNumBonFacture(true);
    setNumBonFactureError("");
    try {
      await updateChantier(chantierId, {
        numBonFacture: null,
        sousEtat: null,
      });
      setChantiers((prev) =>
        prev.map((c) =>
          (c._id === chantierId || c.id === chantierId)
            ? { ...c, numBonFacture: null, sousEtat: null }
            : c
        )
      );
      closeInvoicePopup();
    } catch (e) {
      setNumBonFactureError(
        e?.response?.data?.error ||
          e?.response?.data?.message ||
          "Erreur lors de la suppression du bon de facture."
      );
    } finally {
      setSavingNumBonFacture(false);
    }
  };

  // Save numBonFacture to backend
  const handleSaveNumBonFacture = async () => {
    if (!invoicePopup.chantier) return;
    if (!numBonFactureInput.trim()) {
      setNumBonFactureError("Le numéro de bon de facture est obligatoire.");
      return;
    }
    setSavingNumBonFacture(true);
    setNumBonFactureError("");
    try {
      await updateChantier(invoicePopup.chantier._id || invoicePopup.chantier.id, {
        numBonFacture: numBonFactureInput.trim(),
        sousEtat: 'facturé',
      });
      setChantiers((prev) =>
        prev.map((c) =>
          (c._id === (invoicePopup.chantier._id || invoicePopup.chantier.id) ||
            c.id === (invoicePopup.chantier._id || invoicePopup.chantier.id))
            ? { ...c, numBonFacture: numBonFactureInput.trim(), sousEtat: 'facturé' }
            : c
        )
      );
      closeInvoicePopup();
    } catch (e) {
      setNumBonFactureError(
        e?.response?.data?.error ||
          e?.response?.data?.message ||
          "Erreur lors de la sauvegarde du numéro de bon de facture."
      );
    } finally {
      setSavingNumBonFacture(false);
    }
  };

  // Handler for card click
  const handleCardClick = (chantier) => {
    setSelectedChantier(chantier);
    // Fetch charges for popup - using direct fetch since charges API service not implemented yet
    setPopupLoading(true);
    fetch(
      `${API_URL}/api/charges/chantier/${chantier._id || chantier.id}`
    )
      .then((res) => res.json())
      .then((data) => setPopupCharges(Array.isArray(data) ? data : []))
      .catch(() => setPopupCharges([]))
      .finally(() => setPopupLoading(false));
  };

  const closeChantierPopup = () => {
    setSelectedChantier(null);
    setPopupCharges([]);
  };

  // CSV export helper
  const exportCsv = (rows, filename) => {
    if (!rows || rows.length === 0) return;
    const BOM = "\uFEFF";
    const sep = ",";
    const fmtDate = (v) => v ? new Date(v).toLocaleDateString("fr-FR") : "";
    const esc = (v) => {
      const s = v == null ? "" : String(v);
      if (s === "") return "";
      if (s.includes(sep) || s.includes('"') || s.includes("\n") || s.includes("\r")) {
        return '"' + s.replace(/"/g, '""') + '"';
      }
      return s;
    };

    const headers = ["Nom", "N° Bon de commande", "Client", "Nature", "Date début", "Date fin"];
    const buildRow = (c) => [
      c.nomChantier,
      c.numAttachement,
      c.client,
      c.natureTravail,
      fmtDate(c.dateDebut),
      fmtDate(c.dateFin),
    ];

    const csvRows = rows.map((c) => buildRow(c).map(esc).join(sep));
    const csv = BOM + [headers.map(esc).join(sep), ...csvRows].join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Removed unused handleChangeStatus to fix linter warning

  // Filter and search logic
  const filteredChantiers = chantiers.filter((c) => {
    const matchesSearch = c.nomChantier
      .toLowerCase()
      .includes(search.toLowerCase());
    const matchesNumAttachement = c.numAttachement
      ? c.numAttachement.toLowerCase().includes(searchNumAttachement.toLowerCase())
      : false;
    const matchesAdresseExecution = c.adresseExecution
      ? c.adresseExecution.toLowerCase().includes(searchAdresseExecution.toLowerCase())
      : false;
    const numeroCommandeVal = c.numeroCommande ?? c.numero_commande ?? "";
    const matchesNumeroCommande = numeroCommandeVal !== ""
      ? String(numeroCommandeVal).toLowerCase().includes(searchNumeroCommande.toLowerCase())
      : false;
    // Only filter if search fields are not empty
    const nameOk = !search || matchesSearch;
    const numOk = !searchNumAttachement || matchesNumAttachement;
    const adresseOk = !searchAdresseExecution || matchesAdresseExecution;
    const numCmdOk = !searchNumeroCommande || matchesNumeroCommande;
    if (filter === "actifs") {
      return c.etat === "en cours" && nameOk && numOk && adresseOk && numCmdOk;
    }
    if (filter === "factures") {
      return c.sousEtat === "facturé" && nameOk && numOk && adresseOk && numCmdOk;
    }
    if (filter === "non_factures") {
      return c.etat === "fermé" && c.sousEtat !== "facturé" && !c.numBonFacture && nameOk && numOk && adresseOk && numCmdOk;
    }
    return nameOk && numOk && adresseOk && numCmdOk;
  });

  // NEW: robust date-only normalizer to YYYY-MM-DD (avoids timezone issues)
  const toYMD = (v) => {
    if (!v) return null;
    // Accept Date, ISO, or 'YYYY-MM-DD'
    let s = typeof v === 'string' ? v : (() => {
      const d = new Date(v);
      return isNaN(d.getTime()) ? '' : d.toISOString();
    })();
    if (!s) return null;
    const ymd = s.slice(0, 10);
    return /^\d{4}-\d{2}-\d{2}$/.test(ymd) ? ymd : null;
  };

  // Filter chantiers by date range using Date début (inclusive)
  const dateFilteredChantiers =
    dateStart || dateEnd
      ? filteredChantiers.filter((chantier) => {
          // Only use Date début for filtering
          const chantierYmd = toYMD(chantier.dateDebut);
          if (!chantierYmd) return false; // exclude if no start date while filter is active

          let s = dateStart ? toYMD(dateStart) : null;
          let e = dateEnd ? toYMD(dateEnd) : null;

          // If both provided and end < start, swap to make it robust
          if (s && e && e < s) {
            const tmp = s; s = e; e = tmp;
          }

          const afterStart = !s || chantierYmd >= s;
          const beforeEnd = !e || chantierYmd <= e;

          return afterStart && beforeEnd;
        })
      : filteredChantiers;

  // Pagination for active chantiers
  const activeChantiers = dateFilteredChantiers.filter(
    (c) => c.etat !== "fermé"
  );
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentActiveChantiers = activeChantiers.slice(
    indexOfFirstItem,
    indexOfLastItem
  );
  const totalPages = Math.ceil(activeChantiers.length / itemsPerPage);

  if (loading) {
    return <div className="loading">Chargement des chantiers...</div>;
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Gestion des Chantiers</h1>
        <p className="page-subtitle">
          Gérez efficacement tous vos projets de construction
        </p>
      </div>

      <div className="header-actions">
        <Link to="/add" className="btn btn-primary">
          + Nouveau Chantier
        </Link>
      </div>

      <div className="chantier-controls chantier-controls-multi">
        <input
          type="text"
          placeholder="Nom..."
          value={search}
          onChange={handleSearchChange}
          className="chantier-search-input"
        />
        <input
          type="text"
          placeholder="N° Bon de commande..."
          value={searchNumAttachement}
          onChange={handleSearchNumAttachementChange}
          className="chantier-search-input"
        />
        <input
          type="text"
          placeholder="Numéro de commande..."
          value={searchNumeroCommande}
          onChange={handleSearchNumeroCommandeChange}
          className="chantier-search-input"
        />
        <input
          type="text"
          placeholder="Adresse d’exécution..."
          value={searchAdresseExecution}
          onChange={handleSearchAdresseExecutionChange}
          className="chantier-search-input"
        />
        <select
          value={filter}
          onChange={handleFilterChange}
          className="chantier-filter-select"
        >
          <option value="actifs">Chantiers actifs</option>
          <option value="factures">Chantiers facturés</option>
          <option value="non_factures">Chantiers non facturés</option>
          <option value="tous">Tous les chantiers</option>
        </select>
      </div>

      {/* Date Range Filter */}
      <div className="chantier-controls">
        <label
          htmlFor="date-start"
          style={{ fontWeight: 500, color: "#667eea" }}
        >

          Filtrer (Date début) du :
        </label>
        <input
          id="date-start"
          type="date"
          className="chantier-filter-select"
          value={dateStart}
          onChange={(e) => {
            setDateStart(e.target.value);
            setCurrentPage(1);
          }}
          style={{ minWidth: 0, width: "auto" }}
        />
        <label htmlFor="date-end" style={{ fontWeight: 500, color: "#667eea" }}>
          au :
        </label>
        <input
          id="date-end"
          type="date"
          className="chantier-filter-select"
          value={dateEnd}
          onChange={(e) => {
            setDateEnd(e.target.value);
            setCurrentPage(1);
          }}
          style={{ minWidth: 0, width: "auto" }}
        />
      </div>

      {dateFilteredChantiers.length === 0 ? (
        <div className="empty-state">
          <h3>Aucun chantier trouvé</h3>
          <p>Commencez par ajouter votre premier chantier</p>
          <Link
            to="/add"
            className="btn btn-primary"
            style={{ marginTop: "1rem" }}
          >
            Ajouter un Chantier
          </Link>
        </div>
      ) : (filter === "tous" || filter === "factures" || filter === "non_factures") ? (
        <div className="chantier-table-wrapper">
          {(filter === "factures" || filter === "non_factures") && dateFilteredChantiers.length > 0 && (
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 10,
                background: filter === "factures"
                  ? "linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)"
                  : "linear-gradient(135deg, #e65100 0%, #bf360c 100%)",
                color: "#fff",
                borderRadius: 12,
                padding: "0.65rem 1.4rem",
                marginBottom: 14,
                boxShadow: filter === "factures"
                  ? "0 4px 16px rgba(21,101,192,.30)"
                  : "0 4px 16px rgba(230,81,0,.30)",
                fontSize: "0.95rem",
                fontWeight: 600,
                letterSpacing: ".3px",
              }}
            >
              <span style={{ fontSize: "1.25rem" }}>💰</span>
              <span>{filter === "factures" ? "Chiffre d'affaires facturé" : "Chiffre d'affaires non facturé"}</span>
              <span
                style={{
                  background: "rgba(255,255,255,0.2)",
                  borderRadius: 8,
                  padding: "3px 12px",
                  fontWeight: 800,
                  fontSize: "1.05rem",
                  letterSpacing: ".5px",
                  fontFamily: "ui-monospace, monospace",
                }}
              >
                {dateFilteredChantiers
                  .reduce((sum, c) => sum + (Number(c.prixPrestation) || 0), 0)
                  .toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
              </span>
            </div>
          )}
          {(filter === "factures" || filter === "non_factures") && dateFilteredChantiers.length > 0 && (
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
              <button
                className="btn btn-primary btn-sm"
                onClick={() =>
                  exportCsv(
                    [...dateFilteredChantiers].sort((a, b) => {
                      const dA = a.dateDebut ? new Date(a.dateDebut) : new Date(0);
                      const dB = b.dateDebut ? new Date(b.dateDebut) : new Date(0);
                      return dB - dA;
                    }),
                    filter === "factures"
                      ? "chantiers_factures.csv"
                      : "chantiers_non_factures.csv"
                  )
                }
                style={{
                  background: "linear-gradient(90deg,#27ae60 0%,#2ecc71 100%)",
                  color: "#fff",
                  fontWeight: 700,
                  border: "none",
                  borderRadius: 8,
                  padding: "0.55rem 1.3rem",
                  fontSize: "0.92rem",
                  cursor: "pointer",
                  boxShadow: "0 2px 8px rgba(39,174,96,.25)"
                }}
              >
                📥 Exporter CSV ({dateFilteredChantiers.length})
              </button>
            </div>
          )}
          <table className="chantier-table">
            <thead>
              <tr>
                <th>Nom</th>
                <th>N° Bon de commande</th>
                <th>Client</th>
                <th>Nature</th>
                {/* NEW */}
                <th>Plombier</th>
                <th>Date début</th>
                <th>Heure début</th>
                <th>Date fin</th>
                <th>Heure fin</th>
                <th>État</th>
                <th>Actions</th>
                {/* REMOVE numéro de facture column */}
              </tr>
            </thead>
            <tbody>
              {dateFilteredChantiers.sort((a, b) => {
                const dateA = a.dateDebut ? new Date(a.dateDebut) : new Date(0);
                const dateB = b.dateDebut ? new Date(b.dateDebut) : new Date(0);
                return dateB - dateA; // Most recent first
              }).map((chantier) => {
                const cid = chantier._id || chantier.id || getChantierId(chantier);
                return (
                  <tr key={cid} onClick={() => handleCardClick(chantier)} style={{ cursor: "pointer" }}>
                    <td>{chantier.nomChantier}</td>
                    <td>{chantier.numAttachement}</td>
                    <td>{chantier.client}</td>
                    <td>{chantier.natureTravail}</td>
                    {/* NEW: show plombier name if present */}
                    <td>{chantier.plombier || ""}</td>
                    <td>
                      {chantier.dateDebut
                        ? new Date(chantier.dateDebut).toLocaleDateString("fr-FR")
                        : "-"}
                    </td>
                    <td>{chantier.heureDebut || "-"}</td>
                    <td>
                      {chantier.dateFin
                        ? new Date(chantier.dateFin).toLocaleDateString("fr-FR")
                        : "-"}
                    </td>
                    <td>{chantier.heureFin || "-"}</td>
                    <td>
                      <span
                        className={
                          chantier.etat === "fermé"
                            ? "chantier-etat closed"
                            : chantier.etat === "annulé"
                            ? "chantier-etat canceled"
                            : "chantier-etat active"
                        }
                      >
                        {chantier.etat}
                      </span>
                      {chantier.sousEtat === "facturé" && (
                        <span
                          style={{
                            marginLeft: 6,
                            background: "#1565c0",
                            color: "#fff",
                            borderRadius: 6,
                            padding: "2px 7px",
                            fontSize: "0.78rem",
                            fontWeight: 600,
                          }}
                        >
                          facturé
                        </span>
                      )}
                    </td>
                    <td>
                      <Link to={`/edit/${cid}`} className="btn btn-edit btn-sm">✏️</Link>
                      <Link to={`/charges/${cid}`} className="btn btn-charges btn-sm">💰</Link>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDelete(cid); }}
                        className="btn btn-delete btn-sm"
                      >
                        🗑️
                      </button>
                      {chantier.etat === "fermé" && (
                        <>
                          <button
                            className="btn btn-sm btn-invoice"
                            onClick={(e) => { e.stopPropagation(); handleInvoiceClick(chantier); }}
                          >
                            🧾 Générer Facture
                          </button>
                          {/* NEW: SAV button when fermé */}
                          <button
                            className="btn btn-sm btn-sav"
                            onClick={(e) => { e.stopPropagation(); handleSetSav(cid); }}
                            title="Passer le chantier en SAV"
                          >
                            SAV
                          </button>
                        </>
                      )}
                      {/* NEW: Annuler when en cours */}
                      {chantier.etat === "en cours" && (
                        <button
                          className="btn btn-sm btn-warning"
                          onClick={(e) => { e.stopPropagation(); handleSetAnnule(cid); }}
                        >
                          Annuler le chantier
                        </button>
                      )}
                      {/* Allow closing when en cours or sav */}
                      {chantier.etat !== "fermé" && (
                        <button
                          className="btn btn-close btn-sm"
                          onClick={(e) => { e.stopPropagation(); handleCloseChantier(cid); }}
                        >
                          Fermer le chantier
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <>
          <div className="chantier-grid">
            {currentActiveChantiers.map((chantier) => (
              <ChantierCard
                key={chantier._id || chantier.id || getChantierId(chantier)}
                chantier={chantier}
                onDelete={handleDelete}
                onClose={handleCloseChantier}
                onStatusChange={fetchChantiers}
                onCardClick={handleCardClick}
                // NEW: pass cancel handler to card
                onCancel={handleSetAnnule}
              />
            ))}
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button
                className="pagination-btn"
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                ← Précédent
              </button>

              <div className="pagination-info">
                Page {currentPage} sur {totalPages}
              </div>

              <button
                className="pagination-btn"
                onClick={() =>
                  setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                }
                disabled={currentPage === totalPages}
              >
                Suivant →
              </button>
            </div>
          )}
        </>
      )}

      <Popup
        isOpen={deletePopup.isOpen}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        title="Confirmer la suppression"
        type="warning"
        showCancel={true}
      >
        <div className="delete-confirmation">
          <p>Êtes-vous sûr de vouloir supprimer ce chantier ?</p>
          {deletePopup.chantier && (
            <div className="chantier-name">
              "{deletePopup.chantier.nomChantier}"
            </div>
          )}
          <div className="warning-text">⚠️ Cette action est irréversible !</div>
        </div>
      </Popup>

      <Popup
        isOpen={invoicePopup.isOpen}
        onClose={closeInvoicePopup}
        title="Génération de facture"
        type="info"
      >
        {invoicePopup.chantier && (
          <div
            style={{
              textAlign: "center",
              padding: "1.75rem 1rem 1.25rem",
              background: "#fff",
              borderRadius: "14px",
              border: "1px solid #e5e7eb",
              boxShadow: "0 6px 22px rgba(0,0,0,0.08)",
              margin: "0.25rem auto",
              maxWidth: 460
            }}
          >
            <div
              style={{
                fontSize: "1.05rem",
                fontWeight: 600,
                color: "#1565c0",
                marginBottom: "1.1rem",
                letterSpacing: ".5px"
              }}
            >
              Numéro de Bon de Facture
            </div>

            <div
              style={{
                background: "#f1f4fa",
                border: "1px solid #d5dce5",
                borderRadius: "10px",
                padding: "1rem 1.25rem",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                margin: "0 auto 1rem",
                maxWidth: 320
              }}
            >
              <input
                type="text"
                value={numBonFactureInput}
                onChange={e => {
                  setNumBonFactureError("");
                  setNumBonFactureInput(e.target.value);
                }}
                placeholder="Saisir numéro facture"
                style={{
                  fontSize: "1.55rem",
                  fontWeight: 700,
                  letterSpacing: "2px",
                  color: "#5b3d91",
                  background: "transparent",
                  outline: "none",
                  border: "none",
                  width: "100%",
                  textAlign: "center",
                  fontFamily: "ui-monospace, monospace"
                }}
                autoFocus
              />
            </div>

            {numBonFactureError && (
              <div style={{ color: "#d32f2f", fontSize: ".85rem", marginBottom: 10 }}>
                {numBonFactureError}
              </div>
            )}

            <div style={{ color: "#555", fontSize: ".9rem", marginBottom: 18 }}>
              {invoicePopup.chantier.numBonFacture
                ? "Modifier le numéro de bon de facture si nécessaire."
                : "Entrez et enregistrez le numéro de bon de facture."}
            </div>

            <div style={{ display: "flex", gap: "0.75rem", justifyContent: "center", flexWrap: "wrap" }}>
              <button
                className="btn btn-secondary"
                onClick={closeInvoicePopup}
                disabled={savingNumBonFacture}
                style={{
                  minWidth: 130,
                  background: "#e0e7ef",
                  color: "#374151",
                  fontWeight: 600
                }}
              >
                Annuler
              </button>
              {invoicePopup.chantier.numBonFacture && (
                <button
                  className="btn"
                  onClick={handleDeleteNumBonFacture}
                  disabled={savingNumBonFacture}
                  style={{
                    minWidth: 160,
                    background: "linear-gradient(90deg,#e53935 0%,#d32f2f 100%)",
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: "1rem",
                    border: "none",
                    borderRadius: "10px",
                    padding: "0.85rem 1.75rem",
                    boxShadow: "0 4px 14px rgba(211,47,47,.30)",
                    cursor: savingNumBonFacture ? "not-allowed" : "pointer"
                  }}
                >
                  {savingNumBonFacture ? "Suppression..." : "🗑️ Supprimer"}
                </button>
              )}
              <button
                className="btn"
                onClick={handleSaveNumBonFacture}
                disabled={savingNumBonFacture}
                style={{
                  minWidth: 160,
                  background: "linear-gradient(90deg,#667eea 0%,#3f8fdc 50%,#06b6d4 100%)",
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: "1rem",
                  border: "none",
                  borderRadius: "10px",
                  padding: "0.85rem 1.75rem",
                  boxShadow: "0 4px 14px rgba(66,133,244,.35)",
                  cursor: savingNumBonFacture ? "not-allowed" : "pointer"
                }}
              >
                {savingNumBonFacture ? "Sauvegarde..." : "Enregistrer"}
              </button>
            </div>
          </div>
        )}
      </Popup>

      {/* Chantier Details Popup */}
      <Popup
        isOpen={!!selectedChantier}
        onClose={closeChantierPopup}
        title={
          selectedChantier
            ? `Détails du chantier: ${selectedChantier.nomChantier}`
            : ""
        }
        type="info"
      >
        {selectedChantier && (
          <div className="chantier-details-popup">
            {/* BADGES & STATS */}
            {popupLoading ? (
              <div style={{ color: "#667eea", fontWeight: 500, marginBottom: 8 }}>
                Chargement des données...
              </div>
            ) : (
              (() => {
                const prestation = Number(selectedChantier.prixPrestation || 0);
                // Marge calculée sur les charges RÉELLES uniquement :
                // on exclut l'acompte Achat auto-créé (30%) tant qu'il reste provisoire.
                const isReelleTruthy = (c) => {
                  const v = c?.isReelle;
                  return v === true || v === "true" || v === 1 || v === "1";
                };
                const isAutoThirtyPercentCharge = (c) => {
                  if (!c || c.type !== "Achat") return false;
                  if (c.isAutoThirtyPercent === true) return true;
                  const name = String(c.name || "").toLowerCase();
                  const desc = String(c.description || "").toLowerCase();
                  return (
                    name.includes("30%") ||
                    name.includes("acompte budget") ||
                    desc.includes("30%") ||
                    desc.includes("chiffre d'affaire") ||
                    desc.includes("ajout automatique") ||
                    name.includes("budget travaux") ||
                    desc.includes("budget travaux")
                  );
                };
                const totalCharges = popupCharges
                  .filter((c) => !(isAutoThirtyPercentCharge(c) && !isReelleTruthy(c)))
                  .reduce((sum, c) => sum + (c.budget || 0), 0);
                const brut = prestation - totalCharges;
                const brutPercent = prestation > 0 ? (brut / prestation) * 100 : 0;
                const objectifPercent = 30;
                const objectifAmount = prestation * (objectifPercent / 100);
                const objectifMet = brut >= objectifAmount;
                const rentable = brut > 0;
                const objectifBadge = objectifMet
                  ? { text: "Objectif atteint (≥30% marge)", color: "#27ae60" }
                  : { text: "Objectif non atteint (<30% marge)", color: "#e74c3c" };

                return (
                  <>
                    <div style={{ display: "flex", gap: "0.5rem", marginBottom: 8 }}>
                      {rentable ? (
                        <span
                          style={{
                            background: "#27ae60",
                            color: "white",
                            borderRadius: 6,
                            padding: "2px 8px",
                            fontWeight: 500,
                            fontSize: "0.95rem",
                          }}
                        >
                          ✅ Rentable
                        </span>
                      ) : (
                        <span
                          style={{
                            background: "#e74c3c",
                            color: "white",
                            borderRadius: 6,
                            padding: "2px 8px",
                            fontWeight: 500,
                            fontSize: "0.95rem",
                          }}
                        >
                          ❌ Non rentable
                        </span>
                      )}
                      <span
                        style={{
                          background: objectifBadge.color,
                          color: "white",
                          borderRadius: 6,
                          padding: "2px 8px",
                          fontWeight: 500,
                          fontSize: "0.95rem",
                        }}
                      >
                        {objectifBadge.text}
                      </span>
                    </div>
                    <div style={{ marginBottom: 8 }}>
                      <span style={{ fontWeight: 500, color: "#667eea" }}>
                        Marge brute:
                      </span>{" "}
                      <span style={{ fontWeight: 600 }}>
                        {brut.toLocaleString("fr-FR", {
                          style: "currency",
                          currency: "EUR",
                        })}
                      </span>
                      {"  "}
                      <span
                        style={{
                          color: brutPercent >= 0 ? "#27ae60" : "#e74c3c",
                          fontWeight: 500,
                        }}
                      >
                        ({brutPercent.toFixed(1)}%)
                      </span>
                    </div>
                    <div style={{ marginBottom: 8 }}>
                      <span style={{ fontWeight: 500, color: "#667eea" }}>
                        Objectif 30%:
                      </span>{" "}
                      <span style={{ fontWeight: 600 }}>
                        {objectifAmount.toLocaleString("fr-FR", {
                          style: "currency",
                          currency: "EUR",
                        })}
                      </span>
                      {"  "}
                      <span
                        style={{
                          color: objectifMet ? "#27ae60" : "#e74c3c",
                          fontWeight: 500,
                        }}
                      >
                        ({objectifMet ? "Atteint" : "Non atteint"})
                      </span>
                    </div>
                  </>
                );
              })()
            )}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                marginBottom: 16,
              }}
            >
              <div>
                <div
                  style={{
                    fontWeight: 600,
                    fontSize: "1.1rem",
                    color: "#667eea",
                  }}
                >
                  {selectedChantier.nomChantier}
                </div>
                <div style={{ color: "#888", fontSize: "0.97rem" }}>
                  N° Bon de commande: {selectedChantier.numAttachement}
                </div>
              </div>
            </div>
            <div style={{ marginBottom: 8 }}>
              <span className="info-label">Chiffre d'affaire:</span>{" "}
              {selectedChantier.prixPrestation
                ? Number(selectedChantier.prixPrestation).toLocaleString(
                    "fr-FR",
                    { style: "currency", currency: "EUR" }
                  )
                : "-"}
            </div>
            <div style={{ marginBottom: 8 }}>
              <span className="info-label">Client:</span> {selectedChantier.client}
            </div>
            {/* NEW: Numero de commande */}
            <div style={{ marginBottom: 8 }}>
              <span className="info-label">Numéro de commande:</span> {selectedChantier.numeroCommande || "-"}
            </div>
            <div style={{ marginBottom: 8 }}>
              <span className="info-label">Plombier:</span> {selectedChantier.plombier || "-"}
            </div>
            <div style={{ marginBottom: 8 }}>
              <span className="info-label">Adresse d'exécution:</span> {selectedChantier.adresseExecution || "-"}
            </div>
            <div style={{ marginBottom: 8 }}>
              <span className="info-label">Lieu (Ville):</span> {selectedChantier.lieu || "-"}
            </div>
            <div style={{ marginBottom: 8 }}>
              <span className="info-label">Nature du travail:</span> {selectedChantier.natureTravail}
            </div>
            {/* Date de création removed */}
            <div style={{ marginBottom: 8 }}>
              <span className="info-label">Date début:</span>{" "}
              {selectedChantier.dateDebut
                ? new Date(selectedChantier.dateDebut).toLocaleDateString("fr-FR")
                : "-"}
              {selectedChantier.heureDebut ? ` à ${selectedChantier.heureDebut}` : ""}
            </div>
            <div style={{ marginBottom: 8 }}>
              <span className="info-label">Date fin:</span>{" "}
              {selectedChantier.dateFin
                ? new Date(selectedChantier.dateFin).toLocaleDateString("fr-FR")
                : "-"}
              {selectedChantier.heureFin ? ` à ${selectedChantier.heureFin}` : ""}
            </div>
            <div style={{ marginBottom: 8 }}>
              <span className="info-label">État:</span>{" "}
              <span
                className={
                  selectedChantier.etat === "fermé"
                    ? "chantier-etat closed"
                    : selectedChantier.etat === "annulé"
                    ? "chantier-etat canceled"
                    : "chantier-etat active"
                }
              >
                {selectedChantier.etat}
              </span>
            </div>
          </div>
        )}
      </Popup>
    </div>
  );
};





export default ChantierList;
